/**
 * Public permalink: /sport/:gameId/match/:matchId – single match view. Viewable without login.
 * Shows summary; full stats when user is logged in (via existing app).
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getPublicMatch } from '../../api/readApi';
import { getGameById } from '../../constants/games';
import PublicLayout from '../../components/PublicLayout';
import { useAuth } from '../../contexts/AuthContext';
import { usePlayerCache } from '../../contexts/PlayerCacheContext';
import { formatDateTime } from '../../utils/dateFormat';
import '../PublicPages.css';

function formatScoreSummary(scores) {
  if (!scores) return '—';
  const games = [];
  if (scores.game1) games.push(`${scores.game1.player1 ?? 0}-${scores.game1.player2 ?? 0}`);
  if (scores.game2) games.push(`${scores.game2.player1 ?? 0}-${scores.game2.player2 ?? 0}`);
  if (scores.game3) games.push(`${scores.game3.player1 ?? 0}-${scores.game3.player2 ?? 0}`);
  return games.length ? games.join(', ') : '—';
}

export default function PublicMatchPage() {
  const { gameId, matchId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { getPlayerName } = usePlayerCache();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!matchId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getPublicMatch(matchId)
      .then((res) => {
        if (cancelled) return;
        setMatch(res?.match ?? null);
      })
      .catch(() => {
        if (!cancelled) setMatch(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [matchId]);

  const game = getGameById(gameId);

  if (!game) {
    return (
      <PublicLayout>
        <p>Sport not found.</p>
      </PublicLayout>
    );
  }

  if (loading) {
    return (
      <PublicLayout title={`${game.icon} Match`}>
        <p className="public-loading">Loading…</p>
      </PublicLayout>
    );
  }

  if (!match) {
    return (
      <PublicLayout title="Match">
        <p>Match not found.</p>
        <Link to={`/sport/${gameId}/match`} className="public-link">← Back to matches</Link>
      </PublicLayout>
    );
  }

  const isDoubles = match.matchType === 'doubles';
  const p1Name = isDoubles
    ? (match.team1Name || (match.team1Players || []).map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || 'Team 1')
    : (match.player1Name || (match.player1Id ? getPlayerName(match.player1Id) : null) || 'Player 1');
  const p2Name = isDoubles
    ? (match.team2Name || (match.team2Players || []).map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || 'Team 2')
    : (match.player2Name || (match.player2Id ? getPlayerName(match.player2Id) : null) || 'Player 2');

  return (
    <PublicLayout title={`${game.icon} Match`}>
      <nav className="public-breadcrumb">
        <Link to={`/sport/${gameId}/match`} className="public-link">← {game.name} matches</Link>
      </nav>
      <div className="public-card card-ui">
        <div className="public-match-summary">
          <div className="public-match-side">
            <span className="public-match-player">{p1Name}</span>
          </div>
          <div className="public-match-vs">VS</div>
          <div className="public-match-side">
            <span className="public-match-player">{p2Name}</span>
          </div>
        </div>
        {match.scores && (
          <p className="public-match-score">Score: {formatScoreSummary(match.scores)}</p>
        )}
        <p className="public-match-status">Status: {match.status || 'scheduled'}</p>
        {match.scheduledAt && (
          <p className="public-match-datetime">
            {formatDateTime(match.scheduledAt?.toDate?.() ?? match.scheduledAt)}
          </p>
        )}
        {currentUser && (
          <button
            type="button"
            onClick={() => navigate(`/match/${matchId}`)}
            className="btn-primary"
            style={{ marginTop: 12 }}
          >
            Open in app (score & details)
          </button>
        )}
      </div>
    </PublicLayout>
  );
}
